import moment from 'moment';
import store from '@/store';
import notificationsApi from '@/_api/notifications/notifications.api';
import { TApiListResponse } from '@/_types/api/api-list-response.type';
import { TMessage} from '@/_types/messages.type';
import { NotificationType } from '@/_modules/promo/types/notification-type.enum';
import ContactHelper from '@/_helpers/contact.helper';
import { TMeeting } from '@/_types/meeting/meeting.type';
import broadcastsService from '@/_services/broadcasts.service';
import { TBroadcast } from '@/_types/broadcasts/broadcast.type';
import { TConferenceProgram } from '@/_modules/promo/types/conference-program.type';
import { TConferenceRoom } from '@/_modules/promo/types/conference-room.type';

type TMeetingsReminder = {
  ids: number[];
}

const BADGE_NOTIFICATIONS_INTERVAL = 10000;
const MEETING_REMINDER: TMeetingsReminder = {
  ids: []
};

class NotificationsService {

  private eventId: number;
  private contactId: number;
  private userId: number;
  private badgeNotificationsIntervalId: number;
  private isActive: boolean = false;
  private isChecking: boolean = false;

  public configure(config: { eventId: number; contactId: number; userId: number }): void {
    const eventId = config.eventId || null;
    const contactId = config.contactId || null;
    const userId = config.userId || null;
    if (eventId === this.eventId && contactId === this.contactId && userId === this.userId) {
      return;
    }

    this.eventId = eventId;
    this.contactId = contactId;
    this.userId = userId;

    store.dispatch('notificationsStore/reset');

    if (!this.eventId || !this.contactId || !this.userId) {
      this.isActive = false;
      this.stopBadgeNotificationsInterval();
    } else {
      this.isActive = true;
      this.startBadgeNotificationsInterval();
    }
  }

  private stopBadgeNotificationsInterval(): void {
    if (!this.badgeNotificationsIntervalId) {
      return;
    }
    window.clearInterval(this.badgeNotificationsIntervalId);
    this.badgeNotificationsIntervalId = null;
  }

  private startBadgeNotificationsInterval(): void {
    if (this.badgeNotificationsIntervalId) {
      return;
    }
    this.badgeNotificationsIntervalId = window.setInterval(this.badgeNotificationsCheck.bind(this), BADGE_NOTIFICATIONS_INTERVAL);
  }

  private async badgeNotificationsCheck(): Promise<void> {
    if (!this.isActive || !this.eventId || !this.contactId || !this.userId || this.isChecking) {
      return;
    }

    this.isChecking = true;
    let response;
    try {
      response = await notificationsApi.getUnreadCount(this.eventId);
    } catch {
      /* ignore */
    }

    if (!response) {
      this.isChecking = false;
      return;
    }
    const promises: Promise<void>[] = [];
    if (typeof response.messages_count !== 'undefined') {
      promises.push(
        this.updateMessagesCount(response.messages_count, store.state.notificationsStore.messagesCount)
      );
    }
    if (typeof response.meeting_count !== 'undefined') {
      promises.push(
        this.updateMeetingsCount((response.meeting_count), store.state.notificationsStore.meetingsCount)
      );

      promises.push(
        this.updateMeetingsReminderCount((response.meeting_count), store.state.notificationsStore.meetingsCount)
      );
    }
    // TODO: find a better place for these calls?
    promises.push(
      this.updateBroadcastsNotifications(),
      this.updateProgramSessionsNotifications()
    );
    await Promise.all(promises);
    this.isChecking = false;
  }

  private async updateMessagesCount(newMessagesCount: number, oldMessagesCount: number): Promise<void> {
    if (newMessagesCount === oldMessagesCount) {
      return;
    }
    if (newMessagesCount < oldMessagesCount) {
      store.dispatch('notificationsStore/setMessagesCount', newMessagesCount);
      return;
    }

    const newMessages: TApiListResponse<TMessage> = await store.dispatch('_messageStore/callMessageList', { eventId: this.eventId });
    if (!newMessages || !newMessages.List || !newMessages.List.length) {
      store.dispatch('notificationsStore/setMessagesCount', newMessagesCount);
      return;
    }

    const maxPopupNotifications = 5;
    let notificationNumber = 0;
    let newMessagesCountExpectation = newMessagesCount - oldMessagesCount;
    const incomingMessages: TMessage[] = newMessages.List.filter(message => {
      return message.sender && message.sender.id !== this.contactId;
    });
    incomingMessages.sort((a, b) => {
      return a.created_at > b.created_at ? 1 : -1;
    });

    for (let i = incomingMessages.length - 1; i >= 0; i--) {
      notificationNumber++;
      if (incomingMessages[i].sender) {
        if (!incomingMessages[i].sender.fullName) {
          incomingMessages[i].sender.fullName = ContactHelper.getFullName(incomingMessages[i].sender);
        }
        if (!incomingMessages[i].sender.fullCompany) {
          incomingMessages[i].sender.fullCompany = ContactHelper.getFullCompany(incomingMessages[i].sender);
        }
      }
      store.dispatch('notificationsStore/push', {
        id: 'message_' + incomingMessages[i].id,
        entityId: incomingMessages[i].id,
        type: NotificationType.CONTACT_MESSAGE,
        time: moment(incomingMessages[i].created_at),
        contact: incomingMessages[i].sender,
        skipPopup: notificationNumber > maxPopupNotifications,
      });
      newMessagesCountExpectation--;
      if (newMessagesCountExpectation < 1) {
        break;
      }
    }

    store.dispatch('notificationsStore/setMessagesCount', newMessagesCount);
  }

  private async updateMeetingsCount(newMeetingsCount: number, oldMeetingsCount: number): Promise<void> {
    if (newMeetingsCount === oldMeetingsCount) {
      return;
    }
    if (newMeetingsCount < oldMeetingsCount) {
      store.dispatch('notificationsStore/setMeetingsCount', newMeetingsCount);
      return;
    }

    const newMeetings: TMeeting[] = await store.dispatch('meetingsStore/requestUserMeetings', {
      userId: this.userId,
      force: true,
    });
    if (!newMeetings || !newMeetings || !newMeetings.length) {
      store.dispatch('notificationsStore/setMeetingsCount', newMeetingsCount);
      return;
    }

    const maxPopupNotifications = 5;
    let notificationNumber = 0;
    let newMeetingsCountExpectation = newMeetingsCount - oldMeetingsCount;
    const incomingMeetings: TMeeting[] = newMeetings.filter(meeting => {
      return meeting
        && meeting.status === 'unconfirmed'
        && meeting.creator_contact
        && meeting.creator_contact.id !== this.contactId;
    });
    incomingMeetings.sort((a, b) => {
      return a.id > b.id ? 1 : -1;
    });

    for (let i = incomingMeetings.length - 1; i >= 0; i--) {
      notificationNumber++;
      if (incomingMeetings[i].creator_contact) {
        if (!incomingMeetings[i].creator_contact.fullName) {
          incomingMeetings[i].creator_contact.fullName = ContactHelper.getFullName(incomingMeetings[i].creator_contact);
        }
        if (!incomingMeetings[i].creator_contact.fullCompany) {
          incomingMeetings[i].creator_contact.fullCompany = ContactHelper.getFullCompany(incomingMeetings[i].creator_contact);
        }
      }

      store.dispatch('notificationsStore/push', {
        id: 'meeting_' + incomingMeetings[i].id,
        entityId: incomingMeetings[i].id,
        type: NotificationType.CONTACT_MEETING_REQUEST,
        time: moment(),
        contact: incomingMeetings[i].creator_contact,
        skipPopup: notificationNumber > maxPopupNotifications,
      });
      newMeetingsCountExpectation--;
      if (newMeetingsCountExpectation < 1) {
        break;
      }
    }

    store.dispatch('notificationsStore/setMeetingsCount', newMeetingsCount);
  }

  private async updateMeetingsReminderCount(newMeetingsCount: number, oldMeetingsCount: number): Promise<void> {
    if (newMeetingsCount < oldMeetingsCount) {
      return;
    }

    const newMeetings: TMeeting[] = await store.dispatch('meetingsStore/requestUserMeetings', {
      userId: this.userId,
      force: true,
    });
    if (!newMeetings || !newMeetings.length) {
      return;
    }

    const maxPopupNotifications = 5;
    let notificationNumber = 0;
    let newMeetingsCountExpectation = oldMeetingsCount;

    const remindMeeting: TMeeting[] = newMeetings.filter(meeting => {
      return meeting
       && meeting.status === 'confirmed'
       && meeting.creator_contact
       && !(meeting.is_creator && meeting.contact.id === this.contactId);
    });

    remindMeeting.sort((a, b) => {
      return a.id > b.id ? 1 : -1;
    });

    for (let i = remindMeeting.length - 1; i >= 0; i--) {
      notificationNumber++;
      if (remindMeeting[i].creator_contact) {
        if (!remindMeeting[i].creator_contact.fullName) {
          remindMeeting[i].creator_contact.fullName = ContactHelper.getFullName(remindMeeting[i].creator_contact);
        }
        if (!remindMeeting[i].creator_contact.fullCompany) {
          remindMeeting[i].creator_contact.fullCompany = ContactHelper.getFullCompany(remindMeeting[i].creator_contact);
        }
      }

      const startDate = new Date(remindMeeting[i].date_start);
      const remindTime = new Date(startDate.setMinutes(startDate.getMinutes() - 5));
      const curDate = new Date();

      if(new Date(curDate.setSeconds(0, 0)).valueOf() === new Date(remindTime.setSeconds(0, 0)).valueOf()) {
        const foundedId = MEETING_REMINDER.ids.find((id) => { return remindMeeting[i].id === id; });
        if(foundedId) {
          continue;
        }

        MEETING_REMINDER.ids.push(remindMeeting[i].id);
        store.dispatch('notificationsStore/push', {
          id: 'meeting_' + remindMeeting[i].id,
          entityId: remindMeeting[i].id,
          type: NotificationType.CONTACT_MEETING_REMINDER,
          time: moment(),
          contact: remindMeeting[i].creator_contact,
          skipPopup: notificationNumber > maxPopupNotifications,
        });

        newMeetingsCountExpectation--;
        if (newMeetingsCountExpectation < 1) {
          break;
        }
      }
    }

    store.dispatch('notificationsStore/setMeetingsCount', newMeetingsCount);
  }

  private async updateBroadcastsNotifications(): Promise<void> {

    broadcastsService.enableActiveBroadcastsCheck(this.eventId);
    const broadcasts: TBroadcast[] = broadcastsService.getActiveBroadcasts();

    const maxPopupNotifications = 5;
    let notificationNumber = 0;

    for (let i = 0; i < broadcasts.length; i++) {
      if (!broadcasts[i].details || !broadcasts[i].details.promopage || (this.contactId === broadcasts[i].contactId)) {
        continue;
      }
      // TODO: session notifications work in a separate way, AW-1613, see this.updateProgramSessionsNotifications
      if (broadcasts[i].type === 'program-speaker') {
        continue;
      }
      notificationNumber++;
      store.dispatch('notificationsStore/push', {
        id: 'broadcast_company_' + broadcasts[i].details.promopage.external_id,
        entityId: broadcasts[i].details.promopage.id,
        type: NotificationType.NEW_COMPANY_BROADCAST_STARTED,
        time: moment(),
        external_id: broadcasts[i].details.promopage.external_id,
        skipPopup: notificationNumber > maxPopupNotifications,
      });
    }

  }

  private async updateProgramSessionsNotifications(): Promise<void> {

    const MS_TO_SESSION_START = 7000;
    const nowDate = new Date();
    let conferenceRooms: TConferenceRoom[] = [];
    let sessions: TConferenceProgram[] = [];

    if (!store || !store.getters || !store.getters['promoProgramStore/conferenceRooms']) {
      return;
    }

    conferenceRooms = store.getters['promoProgramStore/conferenceRooms'];
    conferenceRooms.forEach((confRoom) => {
      sessions.push(...confRoom.programs);
    });

    sessions = sessions.filter((session) => {
      const sessionTimestamp: number = ((session.date_start as unknown) as Date).getTime();
      const nowTimestamp: number = nowDate.getTime();
      const millisecondDifference: number = sessionTimestamp - nowTimestamp;
      return ((sessionTimestamp >= nowTimestamp) && (MS_TO_SESSION_START >= millisecondDifference));
    });

    const maxPopupNotifications = 5;
    let notificationNumber = 0;

    for (let i = 0; i < sessions.length; i++) {
      const session = sessions[i];
      if (!session.conference_id || !session.id ) {
        continue;
      }
      notificationNumber++;
      store.dispatch('notificationsStore/push', {
        id: ['conferenceProgram', this.eventId, session.conference_id, session.id].join('_'),
        entityId: session.id,
        type: NotificationType.CONFERENCE_PROGRAM_STARTED,
        time: moment((session.date_start as unknown) as Date),
        isFading: false,
        conferenceProgram: session,
        skipPopup: notificationNumber > maxPopupNotifications,
      });
    }

  }

}

const notificationsService = new NotificationsService();
export default notificationsService;
